import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Expand, Focus, Shrink, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import MemoryNetworkCanvas from './MemoryNetworkCanvas'
import type { MemoryCopy, MemoryGraphPayload, MemoryLinkType, MemoryRow } from './types'
import { toNetworkData } from './utils'

const ALL_LINK_TYPES = new Set<MemoryLinkType>(['semantic', 'temporal', 'entity', 'causal'])

interface Props {
  copy: MemoryCopy
  graph: MemoryGraphPayload
  selected: MemoryRow | null
  highlightedIds: string[]
  focusLabel?: string | null
  onSelect: (row: MemoryRow) => void
  onClearFocus: () => void
}

export default function MemoryNetworkView({ copy, graph, selected, highlightedIds, focusLabel, onSelect, onClearFocus }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const reduceMotion = useReducedMotion()
  const networkData = useMemo(() => toNetworkData(graph), [graph])

  useEffect(() => {
    const handleFullscreen = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current)
    document.addEventListener('fullscreenchange', handleFullscreen)
    return () => document.removeEventListener('fullscreenchange', handleFullscreen)
  }, [])

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => undefined)
    else await wrapperRef.current?.requestFullscreen().catch(() => undefined)
  }

  return (
    <div ref={wrapperRef} data-testid="memory-alive-constellation" className={`memory-neural-stage relative isolate overflow-hidden bg-background ${isFullscreen ? 'h-screen' : 'h-[560px] lg:h-full'}`}>
      <div aria-hidden="true" className="memory-neural-grid pointer-events-none absolute inset-0 z-10" />
      <div aria-hidden="true" className="memory-neural-depth pointer-events-none absolute inset-0 z-10" />
      <motion.div className="pointer-events-none absolute -left-20 top-1/4 z-10 h-80 w-80 rounded-full bg-primary/[0.08] blur-3xl" animate={reduceMotion ? undefined : { x: [0, 90, 20, 0], y: [0, -45, 55, 0], scale: [1, 1.22, 0.94, 1], opacity: [0.35, 0.8, 0.5, 0.35] }} transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="pointer-events-none absolute -right-16 bottom-0 z-10 h-72 w-72 rounded-full bg-primary/[0.05] blur-3xl" animate={reduceMotion ? undefined : { x: [0, -70, 15, 0], y: [0, 35, -50, 0], scale: [0.95, 1.15, 1.02, 0.95], opacity: [0.3, 0.65, 0.45, 0.3] }} transition={{ duration: 17, repeat: Infinity, ease: 'easeInOut' }} />
      <div aria-hidden="true" className="memory-neural-scan pointer-events-none absolute inset-y-0 z-20" />

      <AnimatePresence>
        {focusLabel && <motion.div key={focusLabel} initial={reduceMotion ? false : { opacity: 0, y: -8, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -5 }} className="absolute left-3 top-3 z-30 flex max-w-[70%] items-center gap-2 rounded-full border border-border bg-background/90 px-3 py-1.5 text-[10.5px] text-foreground shadow-xs backdrop-blur-md">
          <Focus className="h-3 w-3 shrink-0 text-primary" />
          <span className="truncate">{copy.inspector.focus}: {focusLabel}</span>
          <Button type="button" variant="ghost" size="icon-sm" onClick={onClearFocus} aria-label={copy.inspector.clear_focus} className="size-5 rounded-full"><X /></Button>
        </motion.div>}
      </AnimatePresence>

      <AnimatePresence>{focusLabel && !reduceMotion && [0, 1].map((ring) => <motion.div key={`pulse-${focusLabel}-${ring}`} className="pointer-events-none absolute inset-[18%] z-20 rounded-full border border-primary/25" initial={{ opacity: 0.58, scale: 0.5 }} animate={{ opacity: 0, scale: 1.5 }} exit={{ opacity: 0 }} transition={{ duration: 1.35, delay: ring * 0.16, ease: 'easeOut' }} />)}</AnimatePresence>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" variant="outline" size="icon-sm" onClick={toggleFullscreen} aria-label={isFullscreen ? copy.canvas.exit_fullscreen : copy.canvas.fullscreen} className="absolute right-3 top-3 z-30 rounded-full bg-background/90 backdrop-blur-md">
            {isFullscreen ? <Shrink /> : <Expand />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">{isFullscreen ? copy.canvas.exit_fullscreen : copy.canvas.fullscreen}</TooltipContent>
      </Tooltip>

      <motion.div className="h-full w-full" initial={reduceMotion ? false : { opacity: 0, scale: 0.975, filter: 'blur(7px)' }} animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }} transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1] }}>
        <MemoryNetworkCanvas data={networkData} variant="constellation" visibleLinkTypes={ALL_LINK_TYPES} colorField="mentioned_at" showLabels selectedId={selected?.id} highlightedIds={highlightedIds} ariaLabel={copy.canvas.constellation_description} onSelect={onSelect} onClear={onClearFocus} />
      </motion.div>
    </div>
  )
}
